import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';

function Login({ onLogin }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'doctor'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleRoleSelect = (role) => {
    setFormData(prev => ({
      ...prev,
      role: role
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic validation
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    // Demo mode - allow login without backend
    // Extract name from email (e.g., john.smith@example.com -> John Smith)
    const emailName = formData.email.split('@')[0];
    const nameParts = emailName.split(/[._-]/);
    const firstName = nameParts[0] ? nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1) : 'User';
    const lastName = nameParts[1] ? nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1) : '';
    
    const demoUser = {
      id: 1,
      email: formData.email,
      first_name: firstName,
      last_name: lastName,
      role: formData.role
    };
    
    localStorage.setItem('user', JSON.stringify(demoUser));
    localStorage.setItem('token', 'demo-token-' + Date.now());
    
    if (onLogin) {
      onLogin(demoUser);
    }
    
    setLoading(false);
    
    // Navigate based on role
    if (formData.role === 'doctor') {
      navigate('/dashboard');
    } else if (formData.role === 'nurse') {
      navigate('/nurse-dashboard');
    } else if (formData.role === 'patient') {
      navigate('/patient-dashboard');
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-shape shape-1"></div>
        <div className="login-shape shape-2"></div>
        <div className="login-shape shape-3"></div>
      </div>
      
      <div className="login-content">
        <div className="login-box">
          <div className="login-header">
            <div className="logo-container">
              <div className="logo-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h1>Medical AI Assistant</h1>
            </div>
            <p className="login-subtitle">Intelligent Clinical Documentation System</p>
          </div>

          <div className="role-selector">
            <h3>Select Your Role</h3>
            <div className="role-buttons">
              <button
                type="button"
                className={`role-button ${formData.role === 'doctor' ? 'active' : ''}`}
                onClick={() => handleRoleSelect('doctor')}
              >
                <div className="role-icon">👨‍⚕️</div>
                <span>Doctor</span>
              </button>
              <button
                type="button"
                className={`role-button ${formData.role === 'nurse' ? 'active' : ''}`}
                onClick={() => handleRoleSelect('nurse')}
              >
                <div className="role-icon">👩‍⚕️</div>
                <span>Nurse</span>
              </button>
              <button
                type="button"
                className={`role-button ${formData.role === 'patient' ? 'active' : ''}`}
                onClick={() => handleRoleSelect('patient')}
              >
                <div className="role-icon">🧑‍🦱</div>
                <span>Patient</span>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Your Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name || ''}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                required
                autoComplete="email"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                className="form-input"
              />
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input type="checkbox" />
                <span>Remember me</span>
              </label>
              <a href="/forgot-password" className="forgot-link">Forgot password?</a>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Signing in...
                </>
              ) : (
                <>Sign In</>
              )}
            </button>
          </form>

          <div className="login-footer">
            <p className="demo-note">Demo Mode: Use any email and password to login</p>
            <div className="security-badge">
              <span>HIPAA Compliant & Secure</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
